import { BaseReportActionsDialog } from '../base-report-actions-dialog';
import { ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { GaValidateService } from '../../../formly/ga-validate.service';
import * as _ from 'underscore';
import { FormattingService } from '../../../services/formatting.service';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../formly/ga-formly.service';
import { ReportDataService } from '../../report-data.service';
import { CustomRateInputComponent } from './addons/custom-rate-input.component';
import { IObject } from '../../../../../type_definitions/datatypes';
import { GaDeferredService, IGADeferred } from '../../../services/ga-deferred.service';
import { Override } from '../../../app-decorators';
import { GaEventService } from '../../../services/ga-event.service';

const RATE_SOURCE_BEST:string = 'BEST_RATE';
const RATE_SOURCE_CUSTOM:string = 'CUSTOM';
const RATE_SOURCE_ORDER:string = 'ORDER';

@Component({
  selector: 'change-invoice-rate-action',
  templateUrl: '../../../message/dialogs/default-form-dialog.html',
  styleUrls: ['./change-invoice-rate-action.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ChangeInvoiceRateActionComponent extends BaseReportActionsDialog implements OnInit {
  public currency:string;
  public invoiceCurrency:string;
  public rate:number;
  public calculatedRate:number;
  public spotRate:number;

  private sameCurrency: boolean;
  private showCustomRate: boolean;
  private isCustomRateError: boolean;
  private customRateLoaded:boolean;
  private rateCustomInputField: IExtraFieldConfigurationObject;

  constructor(
    private formattingService: FormattingService,
    private formlyService: GaFormlyService,
    private reportDataService: ReportDataService,
    private deferredService: GaDeferredService,
    private cdr: ChangeDetectorRef,
    eventService: GaEventService,
    validateService: GaValidateService
  ) {
    super(eventService, validateService);
  }

  @Override(BaseReportActionsDialog)
  public ngOnInit(): void {
    this.dialogClass = 'change-invoice-rate';

    super.ngOnInit();

    this.model = {
      rateSource: RATE_SOURCE_BEST,
      rate: undefined
    };

    // create fields
    this.fields = this.createFields();
  }

  @Override(BaseReportActionsDialog)
  public validate(): Promise<string[] | void> {
    const deferred: IGADeferred<string[] | void> = this.deferredService.defer();

    super.validate()
      .then(() => {
        // rate input field is part of custom component
        if (this.model.rateSource === RATE_SOURCE_CUSTOM && !this.model?.rate) {
          return deferred.reject(['Rate is required']);
        }
        return deferred.resolve();
      })
      .catch((error: string[]) => deferred.reject(error));

    return deferred.promise;
  }

  @Override(BaseReportActionsDialog)
  public getModel():any {
    const params = super.getModel();
    params.baseCurrency = this.currency;
    params.invoiceCurrency = this.invoiceCurrency;
    return params;
  }

  private createFields(): IExtraFieldConfigurationObject[] {
    return [
      this.createSourceRadio('Use best rates', RATE_SOURCE_BEST),
      this.createSourceRadio('Use custom rate (not recommended)', RATE_SOURCE_CUSTOM),
      this.createSourceRadio('Use current rate', RATE_SOURCE_ORDER),
      this.createCustomRateInput(),
      this.formlyService.aTemplate(`
        <div class="alert alert-warning">
          <i class="fa fa-warning"></i>
          Please note that by doing this action the complete invoice will be re-created.<br>
          You will not be able to undo this action!
        </div>`
      )
        .build()
    ];
  }

  public loadSpotRate = (): void => {
    this.model.rate = this.spotRate;
    this.calculateRates();
  };

  private createSourceRadio(label: string, option: string): IExtraFieldConfigurationObject {
    return this.formlyService.aRadioButton('rateSource', label)
      .isRequired()
      .withOption(option)
      .withoutWrapper()
      .onChange(this.rateSourceChanged)
      .withId(`rateSource_${option}`)
      .build();
  }

  private createCustomRateInput(): IExtraFieldConfigurationObject {
    this.rateCustomInputField = this.formlyService.aCustom()
      .isHide()
      .withComponent(CustomRateInputComponent)
      .withLabelClass('d-none')
      .withValueClass('col-sm-12')
      .withExtraOptions({
        templateOptions: {
          inputs: {
            currency: this.currency,
            invoiceCurrency: this.invoiceCurrency,
            calculatedRate: this.calculatedRate,
            fields: [
              this.formlyService.anInput('rate', 'Rate')
                .isRequired()
                .withoutWrapper()
                .onChange(this.onRateChange)
                .withPattern(/^\d+(\.\d{1,6})?$/, '"Not a valid rate"')
                .withId('rate')
                .build()
            ],
            model: this.model,
            spotRate: this.spotRate
          },
          outputs: {
            loadSpotRate: this.loadSpotRate,
            onModelChange: (model: IObject) => this.form.patchValue(model)
          }
        }
      })
      .build();

    return this.rateCustomInputField;
  }

  private rateSourceChanged = (): void => {
    //show custom rate form when custom rate radio selected
    const isRateSourceCustom: boolean = this.model.rateSource === RATE_SOURCE_CUSTOM;
    this.rateCustomInputField.hide = true;

    if (isRateSourceCustom) {
      //load currency if necessary
      if (!this.customRateLoaded && !this.loading) {
        this.loading = true;
        this.reportDataService.loadReportActionInfo(this.reportId, this.action.id, this.rowSelection)
          .then((result) => {
            if (result.status === 'ERROR') {
              this.error = result.error;
              this.isCustomRateError = true;
            } else {
              this.rateCustomInputField.hide = false;
              this.currency = result.info.currency;
              this.invoiceCurrency = result.info.invoiceCurrency;
              this.model.rate = result.info.rate;
              this.spotRate = result.info.spotRate;
              this.sameCurrency = result.info.sameCurrency;
              this.updateCustomRateInputBindings(result.info);
              this.calculateRates();
              this.showCustomRate = true;
              this.validateCurrency();
            }
          })
          .finally(() => {
            this.customRateLoaded = true;
            this.loading = false;
          });

      } else if (!this.loading && !this.error) {
        this.showCustomRate = true;
        this.rateCustomInputField.hide = false;
        this.validateCurrency();
      }
    } else {
      this.showCustomRate = false;
      this.validateCurrency();
    }

    this.cdr.detectChanges();
  };

  private onRateChange = (): void => {
    this.calculateRates();
  };

  private calculateRates = (): void => {
    this.calculatedRate = this.isValidRate(this.model.rate)? Number(this.formattingService.formatNumber(1 / Number(this.model.rate), '0.######')) : undefined;
    this.rateCustomInputField.templateOptions.inputs.calculatedRate = this.calculatedRate;
  };

  private updateCustomRateInputBindings(rateInfo: IObject): void {
    this.rateCustomInputField.templateOptions.inputs.spotRate = rateInfo.spotRate;
    this.rateCustomInputField.templateOptions.inputs.invoiceCurrency = rateInfo.invoiceCurrency;
    this.rateCustomInputField.templateOptions.inputs.currency = rateInfo.currency;
    this.rateCustomInputField.templateOptions.inputs.model = this.model;
  }

  private isValidRate(rate: string): boolean {
    return rate && Number(rate) && !_.isNaN(Number(rate));
  }

  private validateCurrency(): void {
    if (this.sameCurrency && this.showCustomRate) {
      this.error = 'Order currency is the same as invoice currency!';
    } else {
      this.error = this.isCustomRateError ? this.error : '';
    }
  }
}